// src/store/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../services/api';

// ── Thunks ────────────────────────────────────────────────────────────────────
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const { data } = await authAPI.login(username, password);
      // Support both { token, user } and { user: { token, ...user } } shapes
      const token = data.accessToken || data.user?.accessToken;
      const user  = data.userData  || data;
      if (!token) throw new Error('No token received');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      return { token, user };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.response?.data?.errors?.body?.[0] ||
        err.message ||
        'Login failed'
      );
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
});

// ── Slice ─────────────────────────────────────────────────────────────────────
const storedUser  = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
const storedToken = localStorage.getItem('token');

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:    storedUser  || null,
    token:   storedToken || null,
    loading: false,
    error:   null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending,   (s) => { s.loading = true;  s.error = null; })
      .addCase(login.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.token   = payload.token;
        s.user    = payload.user;
      })
      .addCase(login.rejected,  (s, { payload }) => {
        s.loading = false;
        s.error   = payload;
      })
      .addCase(logout.fulfilled, (s) => {
        s.token = null;
        s.user  = null;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectAuth      = (s) => s.auth;
export const selectUser      = (s) => s.auth.user;
export const selectToken     = (s) => s.auth.token;
export const selectAuthLoading = (s) => s.auth.loading;
export const selectAuthError   = (s) => s.auth.error;
